/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import {
  FiUser as RawFiUser,
  FiLock as RawFiLock,
  FiEye as RawFiEye,
  FiEyeOff as RawFiEyeOff,
} from "react-icons/fi";
import { useMsal } from "@azure/msal-react";
import * as S from "./LoginPage.styles";
import { useNavigate } from "react-router-dom";
import { loginNomina, login365 } from "../../api/api";
import notify from "devextreme/ui/notify";
import { apiService } from "../../services/apiService";
import logoImg from "../../assets/img/infra-logo2.svg";

type ApiResponse = {
  status: number;
  data: string;
};

const FiUser = RawFiUser as unknown as React.FC<{ size?: number }>;
const FiLock = RawFiLock as unknown as React.FC<{ size?: number }>;
const FiEye = RawFiEye as unknown as React.FC<{ size?: number }>;
const FiEyeOff = RawFiEyeOff as unknown as React.FC<{ size?: number }>;

const Login: React.FC = () => {
  const [user, setUser] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);

  const [loginAttempts, setLoginAttempts] = useState<number>(0);
  const [isLockoutModalOpen, setIsLockoutModalOpen] = useState<boolean>(false);

  const { instance } = useMsal();
  const navigate = useNavigate();

  const normalizeResponse = (response: any): ApiResponse => {
    return response?.data?.status !== undefined ? response.data : response;
  };

  const handleAzureLogin = () => {
    instance
      .loginPopup({
        scopes: ["user.read"],
      })
      .then(async (response) => {
        console.log("login success", response.account.username);

        const values = {
          correo: response.account.username,
          nombre: response.account.name,
        };

        if (values.correo && values.nombre) {
          const responseLogin = await login365(values.correo);

          if (responseLogin.status === 1) {
            notify("Inicio de sesión éxito.", "success", 2000);
            navigate("/inicio");
          } else if (responseLogin.status === 2) {
            notify(`${responseLogin.data}`, "danger", 2000);
          }
        } else {
          console.log("No se pudo iniciar sesión, faltan datos.");
        }
      })
      .catch((error: any) => {
        console.log("error login", error);
      });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!captchaValue) {
      notify("Por favor, completa el reCAPTCHA.", "info", 2000);
      return;
    }

    if (loginAttempts >= 5) {
      setIsLockoutModalOpen(true);
      return;
    }

    const handleFailedAttempt = () => {
      const newAttemptCount = loginAttempts + 1;
      setLoginAttempts(newAttemptCount);

      if (newAttemptCount >= 5) {
        setIsLockoutModalOpen(true);
      } else {
        const attemptsLeft = 5 - newAttemptCount;
        notify(
          `Credenciales incorrectas o error. Quedan ${attemptsLeft} ${
            attemptsLeft === 1 ? "intento" : "intentos"
          }.`,
          "warning",
          3500,
        );
      }
    };

    try {
      const response = await loginNomina(user, password);
      const payload = normalizeResponse(response);

      if (payload && payload.status === 1) {
        notify("Inicio de sesión éxito.", "success", 2000);
        setLoginAttempts(0);
        navigate("/inicio");
        return;
      }

      if (payload && payload.status === 2) {
        notify(
          typeof payload.data === "string"
            ? payload.data
            : "No se pudo iniciar sesión. Verifica tus datos.",
          "warning",
          3000,
        );
        return;
      }

      handleFailedAttempt();
    } catch (error: any) {
      console.error("Error en la llamada de login:", error);

      const payload = error?.response?.data;

      if (payload?.status === 2 && payload?.data) {
        notify(
          typeof payload.data === "string" ? payload.data : "No autorizado.",
          "warning",
          3000,
        );
        return;
      }

      handleFailedAttempt();
    }
  };

  const handleAcceptLockout = async () => {
    setIsLockoutModalOpen(false);

    try {
      await apiService.patch(`/api/Usuarios/cambiastatus/${user}`, "");
      notify(
        "Tu cuenta ha sido bloqueada. Por favor comunícate con tu administrador.",
        "error",
        4000,
      );
    } catch (error) {
      console.error("Error al intentar bloquear el usuario:", error);
      notify(
        "No se pudo completar la acción. Contacta al administrador.",
        "error",
        4000,
      );
    }
  };

  const handleCaptchaChange = (value: string | null) => {
    setCaptchaValue(value);
  };

  const siteKeys = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  if (!siteKeys) {
    return <p>Error: La clave de reCAPTCHA no está configurada.</p>;
  }

  const isSubmitDisabled =
    !user || !password || !captchaValue || loginAttempts >= 5;

  const shouldShowRecaptcha = user.trim() !== "" && password.trim() !== "";

  return (
    <S.PageContainer>
      <S.BrandingPanel>
        <S.InfraLogo src={logoImg} alt="Logo INFRA" />
        <S.Slogan>
          Garantizamos el suministro de nuestros gases en todo México
        </S.Slogan>
      </S.BrandingPanel>

      <S.FormPanel>
        <S.FormContainer onSubmit={handleSubmit}>
          <S.Title>App acetileno bienvenido</S.Title>
          <S.Subtitle>
            Ingresa tus credenciales para acceder al portal.
          </S.Subtitle>

          <S.InputGroup>
            <S.InputIcon>
              <FiUser size={20} />
            </S.InputIcon>
            <S.Input
              type="text"
              placeholder="Nómina"
              value={user}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setUser(e.target.value)
              }
              required
              maxLength={6}
            />
          </S.InputGroup>

          <S.InputGroup>
            <S.InputIcon>
              <FiLock size={20} />
            </S.InputIcon>
            <S.Input
              type={showPassword ? "text" : "password"}
              placeholder="Contraseña"
              value={password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPassword(e.target.value)
              }
              required
            />
            <S.PasswordToggleIcon
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
            </S.PasswordToggleIcon>
          </S.InputGroup>

          <S.SubmitButton type="submit" disabled={isSubmitDisabled}>
            {loginAttempts >= 5 ? "Cuenta Bloqueada" : "Iniciar Sesión"}
          </S.SubmitButton>

          <S.SecondaryButton type="button" onClick={handleAzureLogin}>
            Iniciar sesión con Microsoft 365
          </S.SecondaryButton>

          {shouldShowRecaptcha && (
            <div style={{ marginTop: "15px" }}>
              <ReCAPTCHA sitekey={siteKeys} onChange={handleCaptchaChange} />
            </div>
          )}
        </S.FormContainer>
      </S.FormPanel>

      {isLockoutModalOpen && (
        <S.ModalOverlay>
          <S.ModalContent>
            <S.ModalHeader>
              <h3>Cuenta Bloqueada</h3>
            </S.ModalHeader>

            <p
              style={{
                textAlign: "center",
                fontSize: "1rem",
                lineHeight: "1.5",
                color: "#333",
              }}
            >
              Tu cuenta ha sido bloqueada por límite de intentos de inicio de
              sesión.
              <br />
              Por favor comunícate con tu administrador.
            </p>

            <S.ModalActions>
              <S.SubmitButton
                type="button"
                onClick={handleAcceptLockout}
                style={{ width: "100%" }}
              >
                Aceptar
              </S.SubmitButton>
            </S.ModalActions>
          </S.ModalContent>
        </S.ModalOverlay>
      )}
    </S.PageContainer>
  );
};

export default Login;
